import React, { useContext, useLayoutEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import * as S from './styles';
import { color } from 'styles/pallete';
import Modal from 'components/Modal';
import DynamicPageContext from 'context/DynamicPage';
import SystemNotifyContext from 'context/SystemNotifyContext';
import LoadGeneralContext from 'context/LoadGeneralContext';
import { UseTranslationProps } from 'types/UseTranslationProps';
import SimpleInput from 'components/SimpleInput';
import InputColor from 'components/InputColor';
import Check from 'components/Buttons/Check';
import { formatDateToISO, formatToDateCustom } from 'utils/functions';
import { editPage, createPage } from './reqs';
import { getPages } from 'pages/DynamicPage/reqs';

export interface DataFormProps {
	name: string;
	header_type: string;
	show_tab_bar: boolean;
	background_color: string;
	secondary_color: string;
	contrast_color: string;
	start_date: string;
	end_date: string;
}

const initialDataForm: DataFormProps = {
	name: '',
	header_type: '',
	show_tab_bar: false,
	background_color: '#fff',
	secondary_color: '#fff',
	contrast_color: '#fff',
	start_date: '',
	end_date: ''
};

interface FormDynamicPagesProps {
	page: number;
	setTotalPage: React.Dispatch<React.SetStateAction<number>>;
	setPage: React.Dispatch<React.SetStateAction<number>>;
}

export default function FormDynamicPages({
	page,
	setPage,
	setTotalPage
}: FormDynamicPagesProps) {
	const { t }: UseTranslationProps = useTranslation();

	const {
		setIsFormOpen,
		isFormOpen,
		setDynamicPageSelected,
		dynamicPageSelected,
		dynamicPagesList,
		setDynamicPagesList
	} = useContext(DynamicPageContext);
	const { handleNotify } = useContext(SystemNotifyContext);
	const { setMessage, setIsOpenLoad } = useContext(LoadGeneralContext);

	const [dataForm, setDataForm] = useState<DataFormProps>(initialDataForm);

	const isDateRangeInvalid =
		dataForm.start_date !== '' &&
		dataForm.end_date !== '' &&
		new Date(dataForm.start_date) > new Date(dataForm.end_date);

	async function handleEdit() {
		if (isDateRangeInvalid) {
			handleNotify(t('dynamicPage.invalidDateRange'), 'error');
			return;
		}

		if (dynamicPageSelected !== null) {
			setMessage('Editando Página');
			setIsOpenLoad(true);

			const { isSuccess, data } = await editPage(
				dataForm,
				dynamicPageSelected,
				handleNotify
			);

			if (isSuccess && data) {
				setIsFormOpen(false);
				const newList = dynamicPagesList.map((page) => {
					if (page.id === data.id) {
						return {
							...page,
							name: dataForm.name,
							header_type: dataForm.header_type,
							show_tab_bar: dataForm.show_tab_bar,
							background_color: dataForm.background_color || '#fff',
							secondary_color: dataForm.secondary_color || '#fff',
							contrast_color: dataForm.contrast_color || '#fff',
							start_date: dataForm.start_date || null,
							end_date: dataForm.end_date || null
						};
					}

					return page;
				});

				setDynamicPagesList(newList);
			}

			setIsOpenLoad(false);
		}
	}

	async function handleCreatePage() {
		if (isDateRangeInvalid) {
			handleNotify(t('dynamicPage.invalidDateRange'), 'error');
			return;
		}

		setIsOpenLoad(true);
		setMessage('Criando Página');
		const { isSuccess, data } = await createPage(dataForm, handleNotify);

		if (isSuccess) {
			await getPages(
				page,
				setTotalPage,
				setPage,
				setDynamicPagesList,
				handleNotify
			);

			// Garante a sincronização dos novos campos na lista local
			if (data && data.id) {
				setDynamicPagesList((prev) =>
					prev.map((item) =>
						item.id === data.id || item.name === dataForm.name
							? {
									...item,
									background_color: dataForm.background_color || '#fff',
									secondary_color: dataForm.secondary_color || '#fff',
									contrast_color: dataForm.contrast_color || '#fff',
									start_date: dataForm.start_date || null,
									end_date: dataForm.end_date || null
								}
							: item
					)
				);
			}

			setIsFormOpen(false);
		}

		setIsOpenLoad(false);
	}

	useLayoutEffect(() => {
		if (dynamicPageSelected !== null) {
			setDataForm({
				header_type: dynamicPageSelected.header_type || '',
				show_tab_bar: dynamicPageSelected.show_tab_bar || false,
				name: dynamicPageSelected.name || '',
				background_color:
					dynamicPageSelected.background_color &&
					dynamicPageSelected.background_color.trim() !== ''
						? dynamicPageSelected.background_color
						: '#fff',
				secondary_color:
					dynamicPageSelected.secondary_color &&
					dynamicPageSelected.secondary_color.trim() !== ''
						? dynamicPageSelected.secondary_color
						: '#fff',
				contrast_color:
					dynamicPageSelected.contrast_color &&
					dynamicPageSelected.contrast_color.trim() !== ''
						? dynamicPageSelected.contrast_color
						: '#fff',
				start_date: dynamicPageSelected.start_date || '',
				end_date: dynamicPageSelected.end_date || ''
			});
		} else {
			setDataForm(initialDataForm);
		}
	}, [dynamicPageSelected, isFormOpen]);

	return (
		<Modal
			isOpenProps={isFormOpen}
			setIsOpenModal={() => {
				setIsFormOpen((value) => !value);
				setDynamicPageSelected(null);
			}}
			title={
				dynamicPageSelected === null
					? t('dynamicPage.newDynamicPage')
					: t('dynamicPage.editDynamicPage')
			}
			buttonClose={false}
		>
			<S.container>
				<div>
					<SimpleInput
						initialValue={dataForm?.name || ''}
						label={t('dynamicPage.name')}
						getValue={(value: string) => {
							setDataForm((obj: DataFormProps) => ({ ...obj, name: value }));
						}}
					/>

					<SimpleInput
						initialValue={dataForm?.header_type || ''}
						label={t('dynamicPage.typeHeader')}
						getValue={(value: string) => {
							setDataForm((obj: DataFormProps) => ({
								...obj,
								header_type: value
							}));
						}}
					/>
				</div>

				<div>
					<Check
						text={t('dynamicPage.isShowTabBar')}
						isChecked={dataForm?.show_tab_bar}
						setIsChecked={() => {
							setDataForm((obj: DataFormProps) => ({
								...obj,
								show_tab_bar: !obj.show_tab_bar
							}));
						}}
						colorCheck={color.interface.green}
					/>
				</div>

				<S.sectionDivider />

				<S.fullWidth>
					<S.sectionTitle>{t('dynamicPage.colorsTitle')}</S.sectionTitle>
					<S.threeColumns>
						<InputColor
							rgbOrExadecimal={false}
							label={t('dynamicPage.backgroundColor')}
							value={dataForm.background_color}
							setValue={(value: string) => {
								setDataForm((obj: DataFormProps) => ({
									...obj,
									background_color: value
								}));
							}}
						/>
						<InputColor
							rgbOrExadecimal={false}
							label={t('dynamicPage.secondaryColor')}
							value={dataForm.secondary_color}
							setValue={(value: string) => {
								setDataForm((obj: DataFormProps) => ({
									...obj,
									secondary_color: value
								}));
							}}
						/>
						<InputColor
							rgbOrExadecimal={false}
							label={t('dynamicPage.contrastColor')}
							value={dataForm.contrast_color}
							setValue={(value: string) => {
								setDataForm((obj: DataFormProps) => ({
									...obj,
									contrast_color: value
								}));
							}}
						/>
					</S.threeColumns>
				</S.fullWidth>

				<S.sectionDivider />

				<S.fullWidth>
					<S.sectionTitle>{t('dynamicPage.scheduleTitle')}</S.sectionTitle>
					<S.twoInputs>
						<SimpleInput
							typeInput="datetime-local"
							label={t('dynamicPage.startDate')}
							initialValue={
								dataForm.start_date === ''
									? ''
									: formatToDateCustom(dataForm.start_date)
							}
							getValue={(value: string) => {
								setDataForm((obj: DataFormProps) => ({
									...obj,
									start_date: value ? formatDateToISO(value) : ''
								}));
							}}
							paddingInLineEnd
						/>

						<SimpleInput
							typeInput="datetime-local"
							label={t('dynamicPage.endDate')}
							initialValue={
								dataForm.end_date === ''
									? ''
									: formatToDateCustom(dataForm.end_date)
							}
							getValue={(value: string) => {
								setDataForm((obj: DataFormProps) => ({
									...obj,
									end_date: value ? formatDateToISO(value) : ''
								}));
							}}
							paddingInLineEnd
						/>
					</S.twoInputs>

					{isDateRangeInvalid && (
						<S.errorText>{t('dynamicPage.invalidDateRange')}</S.errorText>
					)}
				</S.fullWidth>

				<S.containerBtns>
					<S.cancel
						data-testid="cancelBtn"
						onClick={() => {
							setIsFormOpen(false);
							setDynamicPageSelected(null);
						}}
					>
						{t('cancel')}
					</S.cancel>
					<S.save
						data-testid="saveBtn"
						disabled={isDateRangeInvalid}
						onClick={
							dynamicPageSelected === null ? handleCreatePage : handleEdit
						}
					>
						{t('save')}
					</S.save>
				</S.containerBtns>
			</S.container>
		</Modal>
	);
}
